import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { Configuration } from '../../../../shared/configuration/configuration';
import { INITIAL_SESSION_TIMEOUT_STATE, SessionTimeoutState, SessionWarningReason } from './session-timeout-state';

export type SessionExpiryReason = 'manual' | 'expired' | 'token-expired';
export type SessionTimeoutHandler = (reason: SessionExpiryReason) => void;

/** Valores por defecto usados si el env no los define. */
const DEFAULT_TIMEOUTS = {
  idleMs: 25 * 60 * 1000, // inactividad antes del aviso
  graceMs: 5 * 60 * 1000, // periodo de gracia con el aviso visible
} as const;

/**
 * Gestiona el cierre de sesión por inactividad Y por expiración real del token.
 *
 * El antiguo SessionTimeoutService solo controlaba inactividad (25 min + 5 de
 * gracia). Eso dejaba un hueco: si el usuario estaba activo, el frontend nunca
 * cerraba la sesión, pero el JWT del backend (exp ~60 min) expiraba en silencio
 * y el siguiente request moría con un 401 → "Sesión expirada" feo.
 *
 * Esta versión además:
 *  - Recibe la expiración real del token (`setTokenExpiryMs`) y calcula el
 *    deadline efectivo como `min(inactividad + gracia, expiración del token)`.
 *  - Muestra el aviso 5 min (gracia) antes del deadline efectivo, aunque el
 *    usuario esté activo, si el que vence es el token.
 *  - Distingue el motivo del aviso (inactividad vs token) para el mensaje.
 *  - Usa DEADLINES (Date.now + ms) y se recalibra en `visibilitychange`/`focus`,
 *    de modo que la sesión expira de verdad aunque la pestaña esté en segundo
 *    plano (los timers se throttlean en background).
 */
@Injectable()
export class SessionTimeoutNotifier extends BaseNotifier<SessionTimeoutState> {
  private readonly events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  /** Deadline efectivo: inactividad+gracia o expiración del token (el que ocurra antes). */
  private sessionEndDeadline = 0;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private warningDeadline = 0;
  private onLogoutHandler: SessionTimeoutHandler | null = null;
  private running = false;
  /** Expiración real del accessToken (ms) provista por AuthProvider. */
  private tokenExpiryMs: number | null = null;
  /** Motivo del aviso actual. */
  private warningReason: SessionWarningReason = 'inactivity';

  constructor(ref: Ref) {
    super(ref, INITIAL_SESSION_TIMEOUT_STATE);
  }

  // ── API pública ─────────────────────────────────────────────────────────

  setOnLogoutHandler(handler: SessionTimeoutHandler | null): void {
    this.onLogoutHandler = handler;
  }

  /**
   * Registra la expiración real del accessToken. Si el notifier está corriendo,
   * recalibra el deadline (un token que vence antes que la inactividad acorta
   * la sesión, sin importar la actividad del usuario).
   */
  setTokenExpiryMs(ms: number | null): void {
    this.tokenExpiryMs = ms;
    if (this.running && !this.snapshot().warningVisible) {
      this.armIdle();
    }
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.armIdle();
    this.events.forEach((ev) => window.addEventListener(ev, this.onActivity, { passive: true }));
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('focus', this.onVisibility);
  }

  stop(): void {
    this.running = false;
    this.clearTimers();
    this.hideWarning();
    this.events.forEach((ev) => window.removeEventListener(ev, this.onActivity));
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('focus', this.onVisibility);
  }

  /** El usuario confirmó que quiere continuar → reinicia el contador. */
  continueSession(): void {
    if (!this.snapshot().warningVisible) return;
    this.hideWarning();
    this.armIdle();
  }

  /** El usuario pidió cerrar sesión desde el aviso. */
  closeSession(): void {
    if (!this.snapshot().warningVisible) return;
    const reason: SessionExpiryReason = this.warningReason === 'token-expiry' ? 'token-expired' : 'manual';
    this.hideWarning();
    this.running = false;
    this.onLogoutHandler?.(reason);
  }

  // ── Motor de timers resistente a pestañas en segundo plano ─────────────

  private timeoutTimes(): { idleMs: number; graceMs: number } {
    const cfg = this.ref.watch(Configuration);
    const idleMin = cfg.sessionIdleTimeoutMinutes;
    const graceMin = cfg.sessionWarningGraceMinutes;
    return {
      idleMs: Number.isFinite(idleMin) && idleMin > 0 ? idleMin * 60_000 : DEFAULT_TIMEOUTS.idleMs,
      graceMs: Number.isFinite(graceMin) && graceMin > 0 ? graceMin * 60_000 : DEFAULT_TIMEOUTS.graceMs,
    };
  }

  private onActivity = (): void => {
    if (this.snapshot().warningVisible) return;
    this.armIdle();
  };

  /**
   * Programa el aviso para que aparezca `graceMs` antes del deadline efectivo
   * (inactividad + gracia, o expiración del token — el que ocurra primero).
   */
  private armIdle(): void {
    this.clearIdleTimer();
    const { idleMs, graceMs } = this.timeoutTimes();
    const now = Date.now();
    const idleEnd = now + idleMs + graceMs;
    const tokenEnd = this.tokenExpiryMs ?? Infinity;
    this.sessionEndDeadline = Math.min(idleEnd, tokenEnd);
    const warningAt = Math.max(0, this.sessionEndDeadline - graceMs - now);
    this.idleTimer = setTimeout(() => this.onIdleElapsed(), warningAt);
  }

  private onIdleElapsed(): void {
    if (this._disposed || !this.running) return;
    this.showWarning();
  }

  private showWarning(): void {
    if (this.snapshot().warningVisible) return;
    this.clearCountdown();

    const { idleMs, graceMs } = this.timeoutTimes();
    const now = Date.now();
    const idleEnd = now + idleMs + graceMs;
    const tokenEnd = this.tokenExpiryMs ?? Infinity;
    const end = Math.min(idleEnd, tokenEnd);
    this.sessionEndDeadline = end;
    this.warningDeadline = end;
    const total = Math.max(1, Math.round((end - now) / 1000));

    const idleMinutes = Math.max(1, Math.round(idleMs / 60_000));
    const graceMinutes = Math.max(1, Math.round(graceMs / 60_000));
    const isToken = tokenEnd <= idleEnd;
    this.warningReason = isToken ? 'token-expiry' : 'inactivity';

    // Para expiración del token, mostramos los minutos restantes reales (p. ej. si viene de background)
    const remainingMinutes = isToken
      ? Math.max(1, Math.round((end - now) / 60_000))
      : graceMinutes;

    const message = isToken
      ? `Su sesión expirará por seguridad en ${remainingMinutes} ${remainingMinutes === 1 ? 'minuto' : 'minutos'}.`
      : `Han pasado ${idleMinutes} minutos de inactividad. Por seguridad, su sesión se cerrará en ${graceMinutes} ${graceMinutes === 1 ? 'minuto' : 'minutos'} si no responde.`;

    this.setState({
      warningVisible: true,
      totalCountdownSeconds: total,
      countdownSeconds: total,
      message,
      reason: this.warningReason,
    });

    // Tick cada 250ms para que la cuenta recupere el retraso de pestañas en background.
    this.countdownTimer = setInterval(() => this.tick(), 250);
  }

  private tick(): void {
    if (!this.snapshot().warningVisible) return;
    const remaining = Math.max(0, Math.ceil((this.warningDeadline - Date.now()) / 1000));
    if (this.snapshot().countdownSeconds !== remaining) {
      this.updateState((s) => ({ ...s, countdownSeconds: remaining }));
    }
    if (remaining <= 0) this.expireSession();
  }

  private expireSession(): void {
    this.hideWarning();
    this.clearIdleTimer();
    this.running = false;
    this.onLogoutHandler?.(this.warningReason === 'token-expiry' ? 'token-expired' : 'expired');
  }

  private onVisibility = (): void => {
    if (!this.running || this._disposed) return;

    if (this.snapshot().warningVisible) {
      const remaining = Math.max(0, Math.ceil((this.warningDeadline - Date.now()) / 1000));
      this.updateState((s) => ({ ...s, countdownSeconds: Math.min(s.countdownSeconds, remaining) }));
      if (remaining <= 0) this.expireSession();
      return;
    }

    if (this.sessionEndDeadline > 0 && Date.now() >= this.sessionEndDeadline) {
      // El deadline (inactividad o token) venció mientras la pestaña no era visible.
      this.expireSession();
      return;
    }

    // Re-armar el aviso con el tiempo restante real para no depender de timers throttled.
    const { graceMs } = this.timeoutTimes();
    const warningAt = Math.max(0, this.sessionEndDeadline - graceMs - Date.now());
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => this.onIdleElapsed(), warningAt);
  };

  private hideWarning(): void {
    this.clearCountdown();
    if (this.snapshot().warningVisible) {
      this.setState(INITIAL_SESSION_TIMEOUT_STATE);
    }
  }

  private clearIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private clearCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private clearTimers(): void {
    this.clearIdleTimer();
    this.clearCountdown();
  }

  protected override onDispose(): void {
    this.stop();
  }
}